import type { ReactNode } from 'react';
import { useRouter } from 'next/router';
import { ArrowUpRight, Check, LockKeyhole } from 'lucide-react';
import { fromCardBackground } from '../../../lib/card-background-click';
import { useTranslation } from '../../../lib/i18n';
import type { Home3ReadState } from '../../../lib/home3-feed';
import { useMaestroBrief } from '../../../lib/use-maestro-brief';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useOpenSideChat } from '../../../lib/use-open-side-chat';
import { useBriefSlotState } from '../../../lib/use-brief-slot-state';
import { upgradeHref } from '../../../lib/upgrade-origin';
import { useBasePath } from '../../../lib/use-base-path';
import { useActiveConversation } from '../../chat/ActiveConversationContext';
import { WorkspaceBadge } from '../WorkspaceBadge';
import { useViewportNarrow } from '../../../lib/use-narrow';
import { AgentDot } from '../../AgentDot';
import { BriefListenButton } from '../MaestroBrief/BriefListenButton';
import { Button, Card, Typography } from '../ui';
import { HOME3_PAGE } from './Home3Feed';
import styles from './Home3Body.module.css';

/**
 * The brief card — the Brief's own line, the day's headline, and the way into it (Figma
 * `19106:277546`).
 *
 * **It is NOT collapsible, and that is the design's call.** It used to carry a minimize chevron and a
 * one-row collapsed state, from the v3 handoff; the frame draws neither. The card is the first thing on
 * the board and the only place the Brief is offered, so a reader who collapsed it lost the entry point
 * — and a control that hides the screen's own headline earns its place only if the headline is in the
 * way, which on a three-line card it is not. Do not reintroduce it without the frame changing:
 * `test/home3-overview.test.ts` fails on a chevron or a `collapsed` prop here.
 *
 * Two more things about it are load-bearing:
 *
 * **The two numbers come from the feed, not from here.** `closed` and `moving` are passed in, computed
 * once by `summaryNumbers` off the same rows the filter chips count, so the headline and the chips
 * cannot tell two stories about one day. Nothing on this screen may hold a second copy of them.
 *
 * **"Read the full summary" opens the real thing, in the side chat** — the agents' own daily meeting
 * when there is one (the conversation that IS the summary), and otherwise a seeded question to Maestro.
 * It deliberately does NOT expand a longer write-up in place: an inline wall of generated prose is
 * copy nobody can act on, and the chat is where the reader can ask the next question. The label
 * therefore never toggles — there is nothing on this screen left to hide.
 */
export function Home3Summary({
  readState,
  closed,
  moving,
}: {
  /** `ready` is the only state whose numbers mean anything — see `resolveFeedState`. */
  readState: Home3ReadState;
  closed: number;
  moving: number;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  // The title takes the DS Small/Medium role on the DESKTOP only (Figma 19813-28376), aligning it with
  // the connectors rail's "Connected · N" group titles; mobile web keeps its current `h4`. The true
  // viewport decides it — NOT the docked-chat container squeeze, since a desktop with the chat open is
  // still a desktop — read SYNCHRONOUSLY so a mobile load never first paints the desktop weight and then
  // shifts (safe here: the Overview is client-only and never SSR/hydrates).
  const narrow = useViewportNarrow();
  const brief = useMaestroBrief();
  const openSideChat = useOpenSideChat();
  const { openChatWithMessage } = useActiveConversation();
  const router = useRouter();
  const basePath = useBasePath();

  // ## The card has FOUR states, and the decision is not this component's
  //
  // `resolveBriefState` is the shared rule — mirrored 1:1 by both phones — and `useBriefSlotState`
  // gathers its inputs. The catch-up header's `BriefSlot` asks the same question through the same hook,
  // so the two homes cannot disagree about whether the routine is paused.
  //
  // This card used to draw the `hero` and nothing else, so on the Overview home a paused routine, a
  // credit-paused one and a brand-new account all rendered the same "nothing closed and nothing moved"
  // headline — a statement about a day whose meeting never ran (Figma 20051:15881 / 14263 / 15911 and
  // the mobile frame 20051:10903).
  //
  // The three new states reuse the existing `brief.*` copy and CTA behaviour rather than inventing
  // their own: same keys the old home's cards use, so nothing needed translating into 37 bundles, and
  // the chat seed / upgrade destination stay in one place.
  const { state: briefState, workspace } = useBriefSlotState();
  // ## The meta line names the BRIEF, so its agents are the brief's own
  //
  // The line reads "Morning Brief · 8:00 AM · [dots] 3 Agents", and a reader takes that count as who
  // was in THAT meeting — so it has to be, or the card says something it does not mean. The agents
  // behind the DAY (computed by the screen off the same rows the two numbers come from) stay the
  // fallback for an account with no brief, where the card is about the day and nothing else.
  //
  // `brief.agentIds` are IDS already, so there is nothing to resolve and nothing that can fail — the
  // dots and the count are one array and cannot disagree. Ids rather than names because the marks are
  // real `AgentDot`s, keyed by the agent id through common-lib's shared hash, so an agent's dot is the
  // same glyph here as on every other surface and on both phones.
  //
  // ⚠️ **The line is the BRIEF's attendees, and there is no fallback any more.** It used to fall back
  // to the rolling last-24-hours `agentIds` on a card with no brief — harmless while the copy read
  // "{count} Agents", and a false statement the moment Design asked for "Your {count} Agents met this
  // morning" (review). Nobody met: there was no meeting. So one predicate decides the whole meta row,
  // the same one that decides the label and the button, and a card with no brief simply carries its
  // name alone.
  //
  // It is also why an EMPTY list is not a fallback condition: a brief that is still convening is
  // showable before its first agent turn, so `agentIds` is legitimately `[]`, and drawing the day's
  // agents there presented people who were never in that meeting as its attendees.
  const metaAgents = brief ? brief.agentIds : [];
  // `brief.time` is ALREADY the formatted clock string — the hook runs `formatBriefTime` on the
  // conversation's `generatedDate` before handing it over. Passing it through that helper a second
  // time parsed "8:00 AM" as a date, got NaN and returned '', which dropped the label back to the
  // generic one WHILE the button still said "Read the full Brief" — the card naming two different
  // things at once. Every other brief surface reads `time` straight (`MaestroBrief`, the listen modal).
  const briefTime = brief?.time ?? '';
  // ONE predicate decides what this card is about, so the label and the button can never disagree:
  // there is a brief, or there is not.
  const metaLabel = brief
    ? briefTime
      ? t('brief.title', { time: briefTime })
      : t('home.overview.summary.briefNoTime')
    : t('home.overview.summary.title');


  // ## The headline is LINES, not a sentence
  //
  // The design sets it over two (Figma 19106:277546): "Overnight your team closed 12 jobs." then
  // "3 are moving now." They were already two separate i18n keys — the day's two numbers — and joining
  // them with a space to split them again would be string surgery on localized copy. So the break is
  // structural: one element per line, each free to wrap on its own on a narrow pane.
  //
  // A read that is still running, or that failed, has established nothing — so the card says so rather
  // than announcing a quiet day it cannot know about, and those are ONE line because they are one
  // sentence. `quiet` is reserved for a real zero.
  const lines =
    readState === 'error'
      ? [t('home.overview.readFailed')]
      : readState === 'loading'
        ? [t('home.overview.loading')]
        : closed === 0 && moving === 0
          ? [t('home.overview.summary.quiet')]
          : [
              t(closed === 1 ? 'home.overview.summary.closedOne' : 'home.overview.summary.closedOther', { count: closed }),
              t(moving === 1 ? 'home.overview.summary.movingOne' : 'home.overview.summary.movingOther', { count: moving }),
            ];

  // A lock line, a Display 03 title, a body, one button — the shape `activate` and `upgrade` share.
  // Both are OFFERS, so the button is the card's primary action and the headline takes the same role
  // the hero's does, which is what keeps the four states reading as one card.
  const offer = (
    eyebrowIcon: ReactNode,
    eyebrow: string,
    title: string,
    body: string,
    cta: ReactNode,
    onCard: () => void,
  ) => (
    <Card variant="sunken" interactive className={`${styles.summary} ${styles.clickable}`} onClick={fromCardBackground(onCard)}>
      <div className={styles.summaryMeta}>
        {eyebrowIcon}
        <Typography variant={narrow ? 'h4' : 'small-medium'} color={narrow ? 'primary' : 'strong'} component="span">
          {eyebrow}
        </Typography>
      </div>
      <Typography variant="h1" color="primary" component="p" className={styles.summaryHeadline}>
        <span className={styles.summaryHeadlineLine}>{title}</span>
      </Typography>
      <Typography variant="p2" color="secondary" component="p" className={styles.summaryBody}>
        {body}
      </Typography>
      <div className={styles.summaryActions}>{cta}</div>
    </Card>
  );

  if (briefState === 'upgrade') {
    const upgrade = upgradeHref('brief_upgrade');
    const logUpgrade = () => logPageClickBi({ page_name: HOME3_PAGE, button_name: 'brief_upgrade' });
    return offer(
      <LockKeyhole size={16} aria-hidden />,
      t('brief.upgrade.eyebrow'),
      t('brief.upgrade.title'),
      t('brief.upgrade.body'),
      <Button
        size="medium"
        href={upgrade}
        onClick={logUpgrade}
      >
        {t('brief.upgrade.cta')}
      </Button>,
      () => {
        logUpgrade();
        void router.push(upgrade, `${basePath}${upgrade}`);
      },
    );
  }

  // ## The card is never absent — the state only decides what it SAYS
  //
  // By request: the brief card is a fixture of this home, not something that comes and goes. Three
  // paths used to draw nothing at all, which on a cold account meant the home opened with the
  // category tiles as its first row and no explanation of the missing brief:
  //
  //   • `none` — pulses loaded, nothing paused, brief settled with no brief, and NO workspace. That is
  //     an account whose morning routine was never seeded, so the ACTIVATE offer is the true thing to
  //     say: the routine genuinely is not running, and turning it on is the one useful action.
  //   • `newWorkspace` with no workspace to name. Same account, reached by a different route — and the
  //     card must not invent a Space to name (the no-seeded-data rule), so it falls back to the same
  //     offer rather than to a fabricated one.
  //   • `loading` — handled below, where the card holds its own space instead of popping in.
  //
  // ⚠️ The copy is the one loose end, and it is a REAL mismatch rather than a nicety: the shipped
  // eyebrow reads "Re-enable Agents Morning Meetings", which tells an account that never had morning
  // meetings on that it is re-enabling them. The frames' other wording — "Unlock morning meetings" /
  // "Start everyday informed" / "Activate now" (20051:15911) — is exactly right for this case, which
  // is very likely why Design drew both. Picking it up is a 37-bundle copy change; flagged on the PR.
  const offersActivate = briefState === 'activate' || briefState === 'none' || (briefState === 'newWorkspace' && !workspace);

  if (offersActivate) {
    const activate = () => {
      logPageClickBi({ page_name: HOME3_PAGE, button_name: 'brief_activate' });
      // The same ask the old home's card makes: the Maestro turns the routine back on, rather than
      // this card writing the pulse itself.
      openChatWithMessage({ kind: 'new', message: t('brief.activate.chatMessage') });
    };
    return offer(
      <LockKeyhole size={16} aria-hidden />,
      t('brief.activate.eyebrow'),
      t('brief.activate.title'),
      t('brief.activate.body'),
      <Button size="medium" onClick={activate}>
        {t('brief.activate.cta')}
      </Button>,
      activate,
    );
  }

  // ## The first morning: the workspace the agents built, not a day's numbers
  //
  // Its own shape rather than the offer's (Figma 20051:15881): the sentence is the EYEBROW, the
  // workspace is the headline — badge beside name — its descriptor sits under it, and ONE outlined
  // button opens it. The frame drew two equal halves, Growth and Agent Work, from the days a Space had
  // two tabs; Agent Work was folded into the Dashboard tab, so both halves pointed at `workspaceHref`
  // and the split only asked the reader to choose between identical doors.
  // The `&& workspace` is unreachable-with-undefined — `offersActivate` above already claimed the
  // no-workspace case — but it is what lets the compiler narrow, and it keeps this branch honest about
  // needing a Space to name.
  if (briefState === 'newWorkspace' && workspace) {
    const workspaceHref = `/workspace/${encodeURIComponent(workspace.slug)}`;
    const logGrowth = () => logPageClickBi({ page_name: HOME3_PAGE, button_name: 'brief_new_workspace_growth' });
    return (
      <Card
        variant="sunken"
        interactive
        className={`${styles.summary} ${styles.clickable}`}
        onClick={fromCardBackground(() => {
          logGrowth();
          void router.push(workspaceHref, `${basePath}${workspaceHref}`);
        })}
      >
        <div className={styles.summaryMeta}>
          <Check size={16} aria-hidden />
          <Typography variant={narrow ? 'h4' : 'small-medium'} color={narrow ? 'primary' : 'strong'} component="span">
            {t('brief.newWorkspace.title')}
          </Typography>
        </div>
        <div className={styles.summaryWorkspace}>
          <WorkspaceBadge ws={workspace} size={24} />
          {/* Display 04 — the frame's size for the workspace's own name, not the day's Display 03. */}
          <Typography variant="h2" color="primary" component="p" className={styles.summaryWorkspaceName}>
            {workspace.name}
          </Typography>
        </div>
        {workspace.description && (
          <Typography variant="p2" color="secondary" component="p" className={styles.summaryBody}>
            {workspace.description}
          </Typography>
        )}
        {/* One hug button, so no `fullWidth`/split: that pair of rules existed to keep two equal halves
            from running off a phone, and there is only one half now. */}
        <div className={styles.summaryActions}>
          <Button
            variant="outlined"
            size="medium"
            href={workspaceHref}
            onClick={logGrowth}
          >
            {t('brief.newWorkspace.growth')}
          </Button>
        </div>
      </Card>
    );
  }

  const read = () => {
    logPageClickBi({ page_name: HOME3_PAGE, button_name: 'read_full_summary' });
    if (brief) brief.open();
    else openSideChat({ kind: 'new', message: t('home.overview.summary.seed') });
  };

  return (
    <Card
      variant="sunken"
      interactive
      className={`${styles.summary} ${styles.clickable}${narrow ? '' : ` ${styles.summaryDesktop}`}`}
      onClick={fromCardBackground(read)}
    >
      {/* The 20371-58314/58317 redesign (the agents move below the headline, the headline drops to Display
          04, the eyebrow becomes Tiny/Medium) is MOBILE-web + native only — DESKTOP web is deliberately
          unchanged from before it, so the two viewports diverge here on purpose:
          - MOBILE: name · time on one Tiny/Medium line, no agents; the agents get their OWN row below the
            headline (see below); headline is Display 04 (`h2`).
          - DESKTOP: name · time in Small/Medium strong ink with the agents cluster INLINE beside it
            (divider between), exactly as before; headline stays Display 03 (`h1`). */}
      <div className={styles.summaryMeta}>
        <Typography
          variant={narrow ? 'tiny-medium' : 'small-medium'}
          color={narrow ? 'primary' : 'strong'}
          component="span"
        >
          {metaLabel}
        </Typography>
        {/* DESKTOP only: the agents stay on the meta line (pre-redesign), parted from the name by a 1px
            divider — Figma 19813-28376, unaffected by the mobile reposition. */}
        {!narrow && readState === 'ready' && metaAgents.length > 0 && (
          <>
            <span className={styles.metaDivider} aria-hidden />
            <span className={`${styles.summaryAgents} ${styles.summaryAgentsFlush}`}>
              <span className={styles.summaryAvatars}>
                {metaAgents.map((id) => (
                  <span key={id} className={styles.summaryAvatar}>
                    <AgentDot id={id} size={20} ringColor="var(--ds-surface-sunken)" />
                  </span>
                ))}
              </span>
              <Typography variant="small-medium" color="strong" component="span">
                {t(metaAgents.length === 1 ? 'home.overview.summary.agentsOne' : 'home.overview.summary.agentsOther', {
                  count: metaAgents.length,
                })}
              </Typography>
            </span>
          </>
        )}
      </div>
      {/* MOBILE: Display 04 (`h2`, 20/24). DESKTOP: Display 03 (`h1`, 32/34) — unchanged from before the
          redesign. Taken as a role, never the frame's literals, so the mobile weight restore comes for
          free. `component="p"` keeps it a paragraph — the page's one `<h1>` is the title above. */}
      <Typography
        variant={narrow ? 'h2' : 'h1'}
        color="primary"
        component="p"
        className={styles.summaryHeadline}
      >
        {lines.map((line) => (
          <span key={line} className={styles.summaryHeadlineLine}>
            {line}
          </span>
        ))}
      </Typography>

      {/* MOBILE only: the agents who were in the brief get their OWN row BELOW the headline, above the
          buttons (Figma 20371-58317), mirroring both phones. Tiny/Medium 12/16 like the name line. On
          desktop they sit inline on the meta line above instead. */}
      {narrow && readState === 'ready' && metaAgents.length > 0 && (
        <div className={styles.summaryAgentsRow}>
          {/* The dots OVERLAP in their own no-gap box (the row's `gap` would cancel the negative margin);
              -6 on a 20px dot, ring in the card surface — the island cluster's own look. */}
          <span className={styles.summaryAvatars}>
            {metaAgents.map((id) => (
              <span key={id} className={styles.summaryAvatar}>
                <AgentDot id={id} size={20} ringColor="var(--ds-surface-sunken)" />
              </span>
            ))}
          </span>
          <Typography variant="tiny-medium" color="primary" component="span">
            {t(metaAgents.length === 1 ? 'home.overview.summary.agentsOne' : 'home.overview.summary.agentsOther', {
              count: metaAgents.length,
            })}
          </Typography>
        </div>
      )}

      <div className={styles.summaryActions}>
        {/* "Read the full Brief ↗" when a brief exists — it opens that meeting. With no brief the
            button seeds a question to Maestro instead, so it must NOT promise a Brief there; the label
            follows what the click actually does. */}
        <Button
          size="medium"
          endIcon={<ArrowUpRight size={16} aria-hidden className="dir-flip" />}
          onClick={read}
        >
          {t(brief ? 'home.overview.summary.readBrief' : 'home.overview.summary.read')}
        </Button>
        {/* The real read-along, not a fake playing state: the brief already has audio and a follow-along
            modal, and this is that same control. Absent when there is no brief to read. */}
        {brief && <BriefListenButton iconOnly size="medium" />}
      </div>
    </Card>
  );
}
